import { Component, computed, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { IonSpinner, IonInfiniteScroll, IonInfiniteScrollContent } from '@ionic/angular/standalone';
import { IonInfiniteScrollCustomEvent } from "@ionic/core";
import { Subscription } from 'rxjs';
import { StateService } from 'src/app/services/state.service';
import { ProductGridComponent } from '../product-grid/product-grid.component';

import { TElasticItemClient } from '@chemist2u/types-client/C2U/Elastic/default';
import { TLoadPharmacyItemsNew } from '@chemist2u/types-client/C2U/Cloud/item';
import { CustomerSessionDetails } from '@chemist2u/types-client/C2U/ParseObjects';
import { CloudFunctionsService } from 'src/app/services/cloud.functions.service';
import { AmplitudeService } from 'src/app/services/amplitude.service';
import { ButtonComponent } from '../../primitives/button/button.component';
import { UiService } from 'src/app/services/ui.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-product-scroller',
    templateUrl: './product-scroller.component.html',
    styleUrls: ['./product-scroller.component.scss'],
    imports: [ProductGridComponent, IonSpinner, IonInfiniteScroll, IonInfiniteScrollContent, ButtonComponent]
})
export class ProductScrollerComponent implements OnInit, OnDestroy {
  private $amplitude = inject(AmplitudeService);
  private $state = inject(StateService);
  private $cloud = inject(CloudFunctionsService);
  private $ui = inject(UiService);
  private router = inject(Router);

  public isLoggedIn = this.$state.isLoggedIn;
  public hasAddress = this.$state.hasAddress;
  
  private searchText = signal<string | undefined>(undefined);
  private limit = signal<number>(20);
  private categoryId = signal<string | undefined>(undefined);
  private subCategoryId = signal<string | undefined>(undefined);

  @Input({ alias: 'searchText' }) set _searchText(value: string | undefined) {
    if (typeof value == "undefined" || value.length < 3) return;
    this.searchText.set(value);
    this.resetState();
    this.triggerLoadData();
  }

  @Input({ alias: 'limit' }) set _limit(value: number | undefined) {
    this.limit.set(value || 20);
    this.triggerLoadData();
  }

  @Input({ alias: 'categoryId' }) set _categoryId(value: string | undefined) {
    this.categoryId.set(value);
    this.triggerLoadData();
  }

  @Input({ alias: 'subCategoryId' }) set _subCategoryId(value: string | undefined) {
    this.subCategoryId.set(value);
    this.triggerLoadData();
  }

  public state = signal<"loading" | "empty" | "error" | "results">("loading");
  public count = signal<number>(0);
  public page = signal<number>(0);
  public infiniteScrollDisabled = computed<boolean>(() => {
    return this.itemList().length >= this.count();
  });

  protected session = signal<CustomerSessionDetails | undefined>(undefined);
  protected itemList = signal<TElasticItemClient[]>([]);

  protected pharmacyId = computed(() => {
    return this.session()?.pharmacy?.id;
  });

  protected postOnly = computed(() => {
    return this.session()?.pharmacy?.found === false;
  });

  protected searchTextEmpty = computed(() => this.searchText() == undefined || this.searchText()!.trim().length == 0);

  protected loadParams = computed<TLoadPharmacyItemsNew>(() => {
    return {
      ...(this.searchText() ? {
        searchText: this.searchText()!.toLowerCase(),
        query: this.searchText()!.toLowerCase(),
      } : {
        cat: this.categoryId(),
        subcat: this.subCategoryId(),
        sortByField: 'name',
        sortBy: 'asc',
      }),
      pharmacyUserId: this.pharmacyId(),
      page: this.page(),
      limit: this.limit(),
    };
  });
  protected lastLoadParams = signal<string>('');

  sessionSub?: Subscription;

  ngOnInit() {
    this.subSession();
    this.triggerLoadData();
  }

  ngOnDestroy() {
    this.unsubSession();
  }

  private subSession() {
    this.session.set(this.$state.bSession.getValue());
    this.sessionSub = this.$state.bSession.subscribe(session => {
      this.resetState();
      this.session.set(session);
      if (session) this.triggerLoadData(undefined, true);
    });
  }

  private unsubSession() {
    if (this.sessionSub) this.sessionSub.unsubscribe();
  }

  private resetState() {
    this.itemList.set([]);
    this.page.set(0);
  }

  protected triggerLoadData(event?: IonInfiniteScrollCustomEvent<void>, force: boolean = false) {
    const currParams = JSON.stringify(this.loadParams());
    const prevParams = this.lastLoadParams();
    if (prevParams == currParams && !force) return;
    this.lastLoadParams.set(currParams);
    this.debouncedLoadData(event);
  }

  private debounce(fn: Function, delay: number) {
    let timeoutId: NodeJS.Timeout;
    return (event?: IonInfiniteScrollCustomEvent<void>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        fn(event);
      }, delay);
    };
  }

  private debouncedLoadData = this.debounce((event?: IonInfiniteScrollCustomEvent<void>) => {
    this.loadData(event);
  }, 300);

  private async loadData(event?: IonInfiniteScrollCustomEvent<void>) {
    try {
      // this.state.set('loading'); <-- // TODO: Make sure of this
      const result = await this.$cloud.loadPharmacyItems(this.loadParams(), this.postOnly())
      this.count.set(result.count);
      this.itemList.set([...this.itemList(), ...result.items]);
      // this.itemList.set([...this.itemList(), ...result.items.map((i:any) => this.transformItem(i))]);
      const searchText = this.searchText();
      if (searchText != undefined) {
        this.$amplitude.track(this.itemList().length > 0 ? 'ITEM_SEARCH_HAS_RESULTS' : 'ITEM_SEARCH_HAS_NO_RESULTS', { searchTerm: searchText });
      }
      if (this.itemList().length > 0) {
        this.page.set(this.page() + 1);
        this.state.set('results');
      } else {
        this.state.set('empty');
      }
      if (event) {
        event.target.complete();
      }
    } catch (error) {
      this.state.set('error');
      console.error(error);
    }
  }

  public goToAddScript() {
    this.$amplitude.track('ITEM_SEARCH_ADD_SCRIPT');
    if (!this.isLoggedIn()) {
      this.$ui.showLogin.next();
    } else if (!this.hasAddress()) {
      this.$ui.setAddress.next(this.session()?.address);
    } else {
      this.router.navigate(['/add-script']);
    }
  }

  transformItem(item: any) {
    if (item?.override || item?.item_override) {//item -> override
      const override = item?.override || item?.item_override;
      item.netPrice = override.price;
      item.basePrice = override.basePrice;
      item.id = item.objectId
    } else if (item.item) {//override -> item
      const basePrice = item.basePrice;
      const netPrice = item.price;
      const pItem = item.item;
      pItem.netPrice = netPrice;
      pItem.basePrice = basePrice;      

      return pItem;
    } //else item
    return item
  }

}